import { useEffect, useState } from "react";

import ajaxMethods from "../../../../api/ajax-methods";
import { Loader } from "../../../../UI/Loaders/Loaders";
import * as ajaxEndpoints from "../../../../api/ajax-endpoints";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdbreact";
import { currencyFormatter } from "../../../../helpers/stringControllers";
import { attachClassNameToTag } from "../../../../helpers/attachClassNameToTag";
import { accurateQuantifier } from "./../../../../helpers/accurateQuantifier";
import { appInsights } from "../../../../config/appInsights";

import "./loanSchedule.scss";

interface LoanScheduleProps {
  dataRecord: {
    loan_id: number;
    amountApproved: string;
    loan_effective_date: string;
  };
}

type LoanPeriods = {
  dueDate: number[];
  principalLoanBalanceOutstanding: number;
  principalDisbursed: number;
  interestDue: number;
  interestPaid: number;
  penaltyChargesDue: number;
  penaltyChargesOutstanding: number;
  penaltyChargesPaid: number;
  penaltyChargesWaived: number;
  penaltyChargesWrittenOff: number;
  daysInPeriod: number;
  complete: boolean;
  principalDue: number;
  totalDueForPeriod: number;
};

type resultProps = {
  data: null | {
    currency: {
      code: string;
    };
    periods: LoanPeriods[];
    totalPrincipalDisbursed: number;
    totalInterestCharged: number;
    totalPenaltyChargesCharged: number;
    totalPrincipalExpected: number;
    totalRepaymentExpected: number;
  };
  status: "success" | "error" | "warning" | "loading";
  error: boolean;
};

const LoanSchedule = ({ dataRecord }: LoanScheduleProps) => {
  const [result, setResult] = useState<resultProps>();
  const payload = {
    loanId: dataRecord.loan_id,
    principal: Number(dataRecord.amountApproved),
    loanEffectiveDate: dataRecord.loan_effective_date,
  };

  const generateLoanSchedule = async () => {
    setResult({ status: "loading", data: null, error: false });
    try {
      const response = await ajaxMethods.post(
        `${ajaxEndpoints.GENERATE_PENDING_LOAN_SCHEDULE}`,
        payload
      );

      if (response?.data?.status === true) {
        setResult({
          data: response?.data?.data,
          status: "success",
          error: false,
        });
      } else {
        setResult({
          data: null,
          status: "error",
          error: true,
        });
      }
    } catch (error: any) {
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "loanSchedule.tsx",
        },
      });
      setResult({
        data: null,
        status: "error",
        error: true,
      });
    }
  };

  useEffect(() => {
    generateLoanSchedule();
  }, []);

  let RenderedComponent = null;
  if (!result?.data && result?.status === "error") {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> Unable to Generate Loan Schedule
          </h4>
        </div>
      </div>
    );
  } else if (result?.status === "loading") {
    RenderedComponent = (
      <Loader centered="true" text="Fetching loan schedule..." />
    );
  } else {
    if (result?.error) {
      RenderedComponent = (
        <div>
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There was an issue while fetching
              borrower's loans, Please Reload The Page{" "}
            </h4>
          </div>
        </div>
      );
    } else if (result?.data && result?.data.periods.length === 0) {
      RenderedComponent = (
        <div className="centerLoader">
          <div className="grid__padding animated fadeInRight">
            <h4 className="center-txt p-y-5">
              <i className="fas fa-bell" /> There are currently no loans for
              this borrower
            </h4>
          </div>
        </div>
      );
    } else {
      RenderedComponent = (
        <MDBTable responsive minimal="true" bordered hover>
          <MDBTableHead>
            <tr>
              <th>S/N</th>
              <th>Days In Period</th>
              <th>Due Date</th>
              <th>Loan balance Outstanding</th>
              <th>Principal Disbursed</th>
              <th>Principal Due</th>
              <th>Interest Due</th>
              <th>Total Due for Period</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {result?.data &&
              result?.data.periods.map((loanSchedule, index) => {
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{loanSchedule.daysInPeriod}</td>
                    <td>
                      <div style={{ width: "100px" }}>
                        {loanSchedule.dueDate.join("-")}
                      </div>
                    </td>
                    <td>
                      {currencyFormatter(
                        Number(loanSchedule.principalLoanBalanceOutstanding),
                        result?.data?.currency?.code
                      )}
                    </td>
                    <td>
                      {currencyFormatter(
                        Number(loanSchedule.principalDisbursed),
                        result?.data?.currency?.code
                      )}
                    </td>
                    <td>
                      {currencyFormatter(
                        Number(loanSchedule.principalDue),
                        result?.data?.currency?.code
                      )}
                    </td>
                    <td>
                      {currencyFormatter(
                        Number(loanSchedule.interestDue),
                        result?.data?.currency?.code
                      )}
                    </td>
                    <td>
                      {currencyFormatter(
                        Number(loanSchedule.totalDueForPeriod),
                        result?.data?.currency?.code
                      )}
                    </td>
                  </tr>
                );
              })}
            <tr>
              <td className="total">Total</td>
              <td></td>
              <td></td>
              <td></td>
              <td className="total">
                {currencyFormatter(
                  Number(result?.data?.totalPrincipalDisbursed),
                  result?.data?.currency?.code
                )}
              </td>
              <td className="total">
                {currencyFormatter(
                  Number(result?.data?.totalPrincipalExpected),
                  result?.data?.currency?.code
                )}
              </td>
              <td className="total">
                {currencyFormatter(
                  Number(result?.data?.totalInterestCharged),
                  result?.data?.currency?.code
                )}
              </td>
              <td className="total">
                {currencyFormatter(
                  Number(result?.data?.totalRepaymentExpected),
                  result?.data?.currency?.code
                )}
              </td>
            </tr>
          </MDBTableBody>
        </MDBTable>
      );
    }
  }

  return (
    <div className="LoanModal Modal__content">
      <div className="Modal__body" style={{ overflow: "hidden" }}>
        <h3 className="Modal__title p-b-1">
          Loan ID: <span>{dataRecord?.loan_id}</span>
        </h3>

        <div className="rendered-component">{RenderedComponent}</div>
      </div>
    </div>
  );
};

export default LoanSchedule;
